<template lang="pug">
v-container.pa-8
  template(v-if="isFetching")
    v-row(no-gutters)
      v-col(cols="8")
        v-skeleton-loader(type="article@3")
  template(v-else)
    //- For onboarding
    onboarding-review(v-if="!hasReviewAccount" :connectNow.sync="connectNow")
    //- For reconnecting
    google-business-page-setup-flow(v-if="googlePageSetup" redirectQueryParams="?google_page_setup=true" @cancel="cancelGooglePageSetup")
    .title.mb-4
      v-row(no-gutters)
        v-col
          div.h5.basic700--text {{ $t('review_settings') }}
          span.text-sm.font-medium.info500--text.clickable(@click="goToLink") {{ getPageLink() }}
        v-col.shrink
          div.d-flex
            v-btn.heading-7.pr-4.pl-3.text-none(color="basic700" small height="32" outlined style="border-radius: 8px;" @click="goToLink")
              eva-icon.mr-1.mb-n1(name="diagonal-arrow-right-up-outline" height="20" width="20" fill="var(--v-basic700)")
              | {{ $t('go_to_link') }}
            v-btn.heading-7.pr-4.pl-3.ml-2.text-none(color="basic700" small depressed dark height="32" style="border-radius: 8px;" @click="copyUrl" v-clipboard:copy="getPageLink()")
              eva-icon.mr-1.mb-n1(name="copy-outline" height="20" width="20" fill="white" )
              | {{ $t('copy_link') }}
    v-row.pb-2.pb-md-0(no-gutters)
      v-col(cols="12" md="6")
        v-tabs(v-model="settingTab" color="basic700" :height="44" :grow="isMobileScreen")
          v-tab.text-sm.font-semibold.text-capitalize.px-4 {{ $t('review_page') }}
          v-tab.text-sm.font-semibold.text-capitalize.px-4 {{ $t('feedback_form') }}
          v-tab.text-sm.font-semibold.text-capitalize.px-4 {{ $t('sharing') }}
          v-tab.text-sm.font-semibold.text-capitalize.px-4 {{ $t('automation') }}
    v-divider
    v-tabs-items(v-model="settingTab")
      v-tab-item
        v-skeleton-loader(v-if="!hasReviewAccount || googlePageSetup" type="article@3")
        review-page-tab(v-else :isFetching="isFetching" :fetchingData="fetchingData" @googlePageSetup="googlePageSetup = true" :os="os")
      v-tab-item
        feedback-form-tab
      v-tab-item
        sharing-tab
      v-tab-item
        automation-tab(:isFetching="isFetching" :fetchingData="fetchingData")
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import OnboardingReview from '@/components/review/general/OnboardingReview.vue'
import ReviewPageTab from '@/components/review/settings/ReviewPageTab.vue'
import FeedbackFormTab from '@/components/review/settings/FeedbackFormTab.vue'
import SharingTab from '@/components/review/settings/SharingTab.vue'
import AutomationTab from '@/components/review/settings/AutomationTab.vue'
import GoogleBusinessPageSetupFlow from '@/components/review/settings/GoogleBusinessPageSetupFlow.vue'

export default {
  name: 'ReviewSettings',
  components: {
    ReviewPageTab,
    FeedbackFormTab,
    SharingTab,
    OnboardingReview,
    AutomationTab,
    GoogleBusinessPageSetupFlow
  },
  props: {
    os: {
      type: String,
      default: 'review',
      validator(value) {
        return ['clinic', 'donation', 'connect', 'review'].includes(value)
      }
    },
  },
  data() {
    return {
      settingTab: 0,
      isFetching: false,
      fetchingData: null,
      googlePageSetup: false,
      connectNow: false
    }
  },
  computed: {
    ...mapGetters({
      company: 'getCompany',
      hasReviewAccount: 'review/hasFinishedSetup'
    }),
    isMobileScreen() {
      return this.$vuetify.breakpoint.xs
    },
  },
  async mounted() {
    this.isFetching = true
    const allFetching = [this.fetchSettings(), this.fetchGoogleAccount()]
    try {
      await Promise.all([...allFetching])
    } catch (error) {
      this.showMessage(['error', this.$t('error_message.unable_to_complete_request')])
      return
    }
    this.checkRoute()
    this.isFetching = false
  },
  methods: {
    ...mapActions(['showMessage']),
    getPageLink () {
      if (process.env.VUE_APP_ENV === 'prod') {
        return `https://review.yezza.co/review/${this.company.slug}`
      }
      return `https://review-test.yezza.co/review/${this.company.slug}`
    },
    copyUrl() {
      this.showMessage(['success', this.$t('success_message.link_copied_to_clipboard')])
    },
    goToLink () {
      window.open(this.getPageLink(), '_blank')
    },
    async fetchSettings() {
      return this.$axios.get(`/reviews/${this.company.slug}`).then(({ data }) => {
        this.fetchingData = data
        this.$store.commit('review/setSettings', data)
        this.$store.commit('review/setGoogleReviewUrl', data.google_review_url)
      })
    },
    fetchGoogleAccount() {
      return this.$axios.get(`reviews/${this.company.slug}/google`)
        .then(({ data }) => {
          this.$store.commit('review/setMainAccount', data)
        })
        .catch((error) => {
          if (error.response && error.response.status === 404) {
            return
          }
          throw error
        })
    },
    checkRoute() {
      if (this.$route.query?.connect_now === 'true') {
        this.connectNow = true
      }
      if (this.$route.query?.google_page_setup === 'true') {
        this.googlePageSetup = true
      }
      if (!this.hasReviewAccount) {
        return
      }
      if (this.$route.query.tab) {
        switch (this.$route.query.tab) {
          case 'feedback_form':
            this.settingTab = 1
            break;

          case 'sharing':
            this.settingTab = 2
            break;

          case 'automation':
            this.settingTab = 3
            break;
        
          default:
            break;
        }
      }
    },
    cancelGooglePageSetup() {
      this.googlePageSetup = false
    }
  },
}
</script>

<style scoped>
</style>